import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  BookOpenCheck,
  Check,
  ChevronLeft,
  ExternalLink,
  FileCode2,
  FileText,
  ListTree,
  Lock,
  Target,
  Workflow,
} from "lucide-react";
import type { ReactNode } from "react";
import {
  Link,
  type MetaFunction,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/ui/alert";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";

import { ExecutionOrderConcept } from "./concepts/execution-order";
import { GeneralConcept } from "./concepts/general";
import { MakefileDiscoveryConcept } from "./concepts/makefile-discovery";
import { MakefilesConcept } from "./concepts/makefiles";
import { PhonyTargetsConcept } from "./concepts/phony-targets";
import {
  type ConceptDefinition,
  getAdjacentConcepts,
  getConceptDefinition,
  getConceptSection,
} from "./concepts/registry";
import { RuleAnatomyConcept } from "./concepts/rule-anatomy";
import { TargetsAndGoalsConcept } from "./concepts/targets-and-goals";
import { CourseFooter } from "./course-footer";
import { CourseShell } from "./course-shell";
import { InlineText } from "./inline-text";
import {
  buildBillingHref,
  buildLearningHref,
  buildLearningOriginPath,
  buildPricingHref,
  buildRepositoryCommitHref,
  getLearningOrigin,
} from "./navigation";
import type {
  ConceptContentData,
  ConceptLoaderResponse,
  ConceptSlug,
} from "./types";
import { getCourseSectionLabel } from "./utils";

export const conceptMeta: MetaFunction = ({ params }) => {
  const concept = getConceptDefinition(params["concept"]);

  if (!concept) {
    return [{ title: `Concept not found | ${publicEnv.siteTitle}` }];
  }

  return [
    { title: `${concept.title} | ${publicEnv.siteTitle}` },
    { name: "description", content: concept.description },
  ];
};

export function ConceptPage() {
  const {
    access,
    canAccessPaidConcepts,
    concept: slug,
    content,
    dbUser,
    relatedLearning,
  } = useLoaderData<ConceptLoaderResponse>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const concept = getConceptDefinition(slug);
  const adjacentConcepts = getAdjacentConcepts(slug);
  const isLocked = access === "paid" && !canAccessPaidConcepts;

  if (!concept) {
    throw new Error(`Unknown concept: ${slug}`);
  }

  if (!isLocked && !content) {
    throw new Error(`Missing concept content: ${slug}`);
  }

  const conceptSection = getConceptSection(concept.sectionId);
  const origin = getLearningOrigin(searchParams.get("from"));
  const currentPath = buildLearningOriginPath(
    location.pathname,
    location.search,
    location.hash,
  );
  const returnTo = `${location.pathname}${location.search}`;
  const indexHref = conceptSection
    ? `/concepts#${conceptSection.id}`
    : "/concepts";

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-10 pb-20 sm:w-[calc(100%-3rem)] sm:gap-14 sm:pb-28">
        <article className="grid gap-10 pt-8 sm:gap-12 sm:pt-10 lg:pt-12">
          <header className="grid max-w-3xl gap-6">
            <Link
              className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-sm transition"
              to={origin?.href ?? indexHref}
            >
              <ChevronLeft className="size-4" />
              {origin?.label ?? "All concepts"}
            </Link>

            <div className="grid gap-4 sm:grid-cols-[2.5rem_minmax(0,1fr)] sm:gap-5">
              <div className="bg-secondary text-secondary-foreground ring-border/70 flex size-10 items-center justify-center rounded-md shadow-xs ring-1 [&_svg]:size-4">
                <ConceptIcon slug={slug} />
              </div>
              <div className="grid gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    className="w-fit font-mono shadow-xs"
                    variant={access === "free" ? "secondary" : "outline"}
                  >
                    {isLocked && <Lock />}
                    {access === "free" ? "Free concept" : "Full access"}
                  </Badge>
                  {conceptSection && (
                    <Badge
                      asChild
                      className="w-fit shadow-xs"
                      variant="outline"
                    >
                      <Link to={`/concepts#${conceptSection.id}`}>
                        {conceptSection.title}
                      </Link>
                    </Badge>
                  )}
                </div>
                <h1 className="text-4xl leading-tight font-semibold text-balance sm:text-5xl">
                  <InlineText text={concept.title} />
                </h1>
                <p className="text-muted-foreground text-lg leading-8 text-pretty">
                  {concept.description}
                </p>
              </div>
            </div>
          </header>

          {searchParams.get("purchase") === "success" && (
            <Alert>
              <Check className="size-4" />
              <AlertTitle>Full access unlocked</AlertTitle>
              <AlertDescription>
                This concept and the complete learning library are now available
                on your account.
              </AlertDescription>
            </Alert>
          )}

          <div className="sm:pl-[3.75rem]">
            {isLocked ? (
              <ConceptAccessGate
                concept={concept}
                unlockHref={
                  dbUser
                    ? buildBillingHref({
                        returnTo,
                      })
                    : buildPricingHref({
                        returnTo,
                      })
                }
              />
            ) : (
              content && <ConceptContentRenderer content={content} />
            )}
          </div>
        </article>

        <section className="border-border/70 grid gap-6 border-t pt-8 sm:ml-[3.75rem] sm:pt-10">
          <div className="grid gap-3">
            <h2 className="text-xl leading-tight font-semibold">
              See it in context
            </h2>
            <p className="text-muted-foreground max-w-2xl leading-7">
              Follow the lessons that apply this concept, then inspect the
              repository at the exact commit behind each explanation.
            </p>
          </div>

          {relatedLearning.length > 0 && (
            <div className="border-border divide-y border-y">
              {relatedLearning.map(({ lesson, module, repositoryCommit }) => (
                <article
                  key={lesson.id}
                  className="grid gap-4 px-2 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                >
                  <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
                    <span className="bg-secondary text-secondary-foreground ring-border/70 flex size-9 items-center justify-center rounded-md shadow-xs ring-1">
                      <BookOpen className="size-4" />
                    </span>
                    <span className="grid min-w-0 gap-1">
                      <span className="text-muted-foreground text-xs">
                        {getCourseSectionLabel(module)} · commit{" "}
                        {repositoryCommit.shortCommit}
                      </span>
                      <span className="font-medium">
                        <InlineText text={lesson.title} />
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild size="sm" variant="outline">
                      <Link
                        to={buildLearningHref({
                          destination: `/course/${repositoryCommit.shortCommit}`,
                          from: currentPath,
                        })}
                      >
                        Open lesson
                      </Link>
                    </Button>
                    <Button asChild size="sm" variant="ghost">
                      <Link
                        to={buildRepositoryCommitHref({
                          commit: repositoryCommit.commit,
                          from: currentPath,
                        })}
                      >
                        Inspect commit
                      </Link>
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}

          {concept.officialReference && (
            <a
              className="underline-decoration flex w-fit items-center gap-1.5 text-sm underline"
              href={concept.officialReference.href}
              rel="noreferrer"
              target="_blank"
            >
              {concept.officialReference.label ?? "GNU Make manual"}:{" "}
              <InlineText text={concept.officialReference.title} />
              <ExternalLink className="size-3.5" />
            </a>
          )}
        </section>

        <ConceptNavigation
          next={adjacentConcepts.next}
          originPath={origin?.href}
          previous={adjacentConcepts.previous}
        />
      </main>

      <CourseFooter />
    </CourseShell>
  );
}

function ConceptContentRenderer({ content }: { content: ConceptContentData }) {
  switch (content.slug) {
    case "execution-order":
      return <ExecutionOrderConcept content={content} />;
    case "include-paths":
    case "layered-conventions":
    case "make-shell-boundary":
    case "makefiles-and-recursion":
    case "pattern-rules":
    case "prerequisite-freshness":
    case "shell-shared-state":
    case "staged-installation":
    case "variable-expansion":
    case "versioned-releases":
      return <GeneralConcept content={content} />;
    case "makefile-discovery":
      return <MakefileDiscoveryConcept content={content} />;
    case "makefiles":
      return <MakefilesConcept content={content} />;
    case "phony-targets":
      return <PhonyTargetsConcept content={content} />;
    case "rule-anatomy":
      return <RuleAnatomyConcept content={content} />;
    case "targets-and-goals":
      return <TargetsAndGoalsConcept content={content} />;
  }
}

function ConceptAccessGate({
  concept,
  unlockHref,
}: {
  concept: ConceptDefinition;
  unlockHref: string;
}) {
  return (
    <section className="bg-card grid min-h-72 place-items-center rounded-md border p-6 shadow-sm">
      <div className="grid max-w-lg justify-items-center gap-5 text-center">
        <span className="bg-secondary text-secondary-foreground ring-border/70 flex size-12 items-center justify-center rounded-md shadow-xs ring-1">
          <Lock className="size-5" />
        </span>
        <div className="grid gap-2">
          <h2 className="text-xl font-semibold">Continue this concept</h2>
          <p className="text-muted-foreground leading-7">
            The summary remains public. Full access unlocks the complete{" "}
            {concept.title.toLowerCase()} explanation and worked examples.
          </p>
        </div>
        <Button asChild>
          <Link to={unlockHref}>View full-access options</Link>
        </Button>
      </div>
    </section>
  );
}

function ConceptIcon({ slug }: { slug: ConceptSlug }) {
  switch (slug) {
    case "makefiles":
      return <FileText />;
    case "makefile-discovery":
      return <FileCode2 />;
    case "rule-anatomy":
      return <ListTree />;
    case "execution-order":
      return <Workflow />;
    case "targets-and-goals":
      return <Target />;
    case "phony-targets":
      return <BookOpenCheck />;
    case "include-paths":
    case "makefiles-and-recursion":
    case "variable-expansion":
      return <FileCode2 />;
    case "pattern-rules":
    case "prerequisite-freshness":
      return <ListTree />;
    case "make-shell-boundary":
    case "shell-shared-state":
      return <Workflow />;
    case "staged-installation":
    case "versioned-releases":
      return <Target />;
    case "layered-conventions":
      return <BookOpen />;
  }
}

interface ConceptNavigationProps {
  next: ConceptDefinition | undefined;
  originPath: string | undefined;
  previous: ConceptDefinition | undefined;
}

function ConceptNavigation({
  next,
  originPath,
  previous,
}: ConceptNavigationProps) {
  return (
    <nav
      aria-label="Concept navigation"
      className="border-border grid divide-y border-y sm:grid-cols-2 sm:divide-y-0"
    >
      {previous ? (
        <ConceptNavigationLink
          concept={previous}
          direction="previous"
          originPath={originPath}
        >
          <ArrowLeft />
        </ConceptNavigationLink>
      ) : (
        <span className="hidden sm:block" />
      )}
      {next && (
        <ConceptNavigationLink
          concept={next}
          direction="next"
          originPath={originPath}
        >
          <ArrowRight />
        </ConceptNavigationLink>
      )}
    </nav>
  );
}

interface ConceptNavigationLinkProps {
  children: ReactNode;
  concept: ConceptDefinition;
  direction: "next" | "previous";
  originPath: string | undefined;
}

function ConceptNavigationLink({
  children,
  concept,
  direction,
  originPath,
}: ConceptNavigationLinkProps) {
  const isNext = direction === "next";

  return (
    <Link
      className={`hover:bg-accent/60 group grid items-center gap-3 px-3 py-4 transition ${
        isNext
          ? "grid-cols-[minmax(0,1fr)_auto] text-right sm:border-l"
          : "grid-cols-[auto_minmax(0,1fr)]"
      }`}
      to={buildLearningHref({
        destination: `/concepts/${concept.slug}`,
        from: originPath,
      })}
    >
      {!isNext && (
        <span className="text-muted-foreground [&_svg]:size-4">{children}</span>
      )}
      <span className="grid min-w-0 gap-1">
        <span className="text-muted-foreground text-xs">
          {isNext ? "Next concept" : "Previous concept"}
        </span>
        <span className="inline-flex items-center gap-1.5 font-medium">
          <InlineText text={concept.title} />
          {concept.access === "paid" && (
            <Lock className="text-muted-foreground size-3.5" />
          )}
        </span>
      </span>
      {isNext && (
        <span className="text-muted-foreground [&_svg]:size-4">{children}</span>
      )}
    </Link>
  );
}
